import type {
  GeneralInformation,
  VirtualAgentConfiguration,
  CustomerIntent,
  ConversationalFlow,
  ConversationalFlowUI,
  Knowledge,
  Inactivity,
  Experimental,
  ExperimentalUI,
  ExternalToolsUI,
  ExternalToolsPayload,
  CallDurationLimits,
  CallDurationLimitsUI,
  FlagsEnabled,
  Sampling,
  Vad,
  VariableUI,
} from 'shared/src'
import { VoiceIsolationModel } from 'shared/src'
import {
  deserializeExternalTools,
  serializeExternalTools,
} from '@/composables/configurator/externalToolsSerializer'
import {
  createNoMatchIntent,
  isNoMatchIntent,
  useDefaultIntent,
} from '@/composables/configurator/useDefaultIntent.ts'
import {
  DEFAULT_MAIN_SKILLS,
  normalizeMainSkills,
} from '@/composables/configurator/useVirtualAgentMainSkills'
import { STORAGE_KEYS } from '@/enums/StorageEnum'
import { isNotBlank } from '@/utils/String'
import {
  DEFAULT_GENERATION_MODEL,
  GENERATION_MODEL_V2,
  isTranscriptionModel,
  isVoiceIsolationModel,
} from '@/composables/configurator/useExperimentalOptions'

const KNOWN_FEATURE_KEYS = new Set([
  'asr_model',
  'generation_model',
  'voice_isolation_model',
  'noise_suppression_strength',
  'km_embedded_rag',
  'km_enable_rerank',
  'km_expand_query',
  'km_similarity_threshold',
  'km_number_of_doc_chunks',
  'vad_activation_threshold',
  'vad_min_speech_duration',
])

export const serializeAdditionalParams = (
  params: { key: string; value: string }[],
): Record<string, string> | undefined => {
  const entries = params
    .filter(({ key }) => isNotBlank(key))
    .map(({ key, value }) => [key, value] as [string, string])
  return entries.length > 0 ? Object.fromEntries(entries) : undefined
}

export const parseRunToolsOnEnter = (
  raw: string,
): Record<string, Record<string, unknown>> | undefined => {
  if (!isNotBlank(raw)) return undefined
  try {
    return JSON.parse(raw) as Record<string, Record<string, unknown>>
  } catch {
    return undefined
  }
}

/** Raw inactivity payload — supports legacy `reengagement_description` field. */
interface RawInactivity extends Inactivity {
  reengagement_description?: string
}

/** Flags shape before setExperimental UI transformation.
 *  Backend may send `enabled` as string[] (array form) or FlagsEnabled (object form). */
interface RawFlags {
  detection_window?: number
  enabled?: FlagsEnabled | string[]
}

/** Extended experimental payload accepted by setExperimental.
 *  Combines the backend Experimental shape with internal UI-only fields. */
interface RawExperimental extends Omit<Experimental, 'flags' | 'persona_voice_ids_override'> {
  /** Backend-legacy separate vad object (before features.vad_* keys). */
  vad?: Vad
  /** Flags shape before transformation: `enabled` may be string[] from backend. */
  flags?: RawFlags
  /** Sampling stored at root level alongside features. */
  sampling?: Sampling
  /** Private UI field: original agent description forwarded from general_information. */
  _agentDescription?: string
  /** Private UI field: speech config forwarded from VirtualAgentConfiguration.speech. */
  _speech?: VirtualAgentConfiguration['speech']
  /** Legacy class identifier field from older API payloads. */
  class_identifier?: string
  /** Voice IDs override for persona (UI field forwarded from API). */
  persona_voice_ids_override?: unknown
}

/** Parsed draft stored in sessionStorage, including UI-only phrase fields. */
type ConfiguratorDraftPayload = Omit<VirtualAgentConfiguration, 'conversational_flow'> & {
  conversational_flow: VirtualAgentConfiguration['conversational_flow'] &
    Pick<ConversationalFlowUI, 'opening_phrases' | 'closing_phrases'>
}

interface ConfiguratorDraft {
  configId: string | null
  payload: ConfiguratorDraftPayload
}

const isDuplicated = ref<boolean>(false)
export const DEFAULT_GENERAL_INFORMATION = Object.freeze({
  agent_name: '',
  agent_description: '',
  supported_languages: [
    { code: 'fr-FR', audio: '', default: true },
    { code: 'en-US', audio: '', default: false },
    { code: 'de-DE', audio: '', default: false },
    { code: 'es-ES', audio: '', default: false },
  ],
})

export const DEFAULT_CONVERSATION_FLOW = Object.freeze({
  has_welcome_message: true,
  opening_phrases: [],
  has_wrap_up_message: false,
  closing_phrases: [],
  replacements: [],
})

export const DEFAULT_INACTIVITY = Object.freeze({
  user_away_timeout: 10,
  max_retries_user_away: 2,
  re_engagement_description: '',
  inactivity_output: 'CONTACT_UNRESPONSIVE',
})

export const DEFAULT_CALL_DURATION_LIMITS = Object.freeze({
  max_call_duration_seconds: 120,
  max_call_duration_speech: [],
  max_call_duration_output: 'MAX_DURATION',
  max_concurrent_calls: 25,
})

export const DEFAULT_FORM_SCHEMA_FIELD = Object.freeze({
  name: '',
  description: '',
  type: 'string' as const,
})

export const DEFAULT_EXPERIMENTAL = Object.freeze({
  fsm: false,
  form_schema: [{ ...DEFAULT_FORM_SCHEMA_FIELD }],
  sampling: {
    generation_model: DEFAULT_GENERATION_MODEL,
    temperature: 0.3,
    top_k: 50,
    top_p: 0.95,
    max_tokens: 2000,
  },
  flags: {
    detection_window: 3,
    enabled: {
      ok: false,
      confusion: false,
      hostility: false,
      dissatisfaction: false,
      complaint: false,
      vulgarity: false,
      fraud: false,
    },
  },
  tools: {
    end_call: true,
    append_to_session_memory: true,
    text_to_numbers: true,
    set_speech_language: true,
    answer_with_rag: DEFAULT_MAIN_SKILLS.answer_with_rag,
    qualify_call: DEFAULT_MAIN_SKILLS.qualify_call,
    step_validation: DEFAULT_MAIN_SKILLS.step_validation,
    custom_skill: DEFAULT_MAIN_SKILLS.custom_skill,
  },
  transcription_model: '0226-Medium' as const,
  voice_isolation_model: VoiceIsolationModel.DeepFilterNet3,
  noise_suppression_strength: 0.85,
  km_embedded_rag: false,
  km_enable_rerank: true,
  km_expand_query: false,
  km_similarity_threshold: 0.4,
  km_number_of_doc_chunks: 5,
  vad: {
    min_speech_duration: 0.01,
    activation_threshold: 0.25,
  },
  feature_toggles: [],
  contextual_prompt_override: '',
  additional_parameters: [],
  class_identifier_override: '',
  persona_uuid_override: '',
  persona_role_override: '',
  persona_run_tools_on_enter: '',
  persona_voice_ids_override: '',
  speech_interruptions: {
    welcome: true,
    wrap_up: true,
    re_engagement: true,
    max_duration: true,
  },
})

export const DEFAULT_EXTERNAL_TOOLS: ExternalToolsUI = Object.freeze({
  maxToolCallsPerTurn: 5,
  servers: [],
})

const configurationId = ref<string | null | undefined>(null)
const generalInformation = ref<GeneralInformation>({ ...DEFAULT_GENERAL_INFORMATION })
const conversationFlow = ref<ConversationalFlowUI>({ ...DEFAULT_CONVERSATION_FLOW })

const customerIntents = ref<CustomerIntent[]>([])
const inactivity = ref<Inactivity>({ ...DEFAULT_INACTIVITY })
const callDurationLimits = ref<CallDurationLimitsUI>({ ...DEFAULT_CALL_DURATION_LIMITS })
const knowledge = ref<Knowledge>({})
const experimental = ref<ExperimentalUI>(structuredClone(DEFAULT_EXPERIMENTAL))
const externalTools = ref<ExternalToolsUI>(structuredClone(DEFAULT_EXTERNAL_TOOLS))

const initialPayload = ref<VirtualAgentConfiguration | null>(null)
const isEnabled = ref<boolean>(false)
const isCancelModalOpen = ref(false)
const pendingNavigationTo = ref<string | null>(null)

const payload = computed<VirtualAgentConfiguration>(() => {
  const maxCallDurationSpeech = callDurationLimits.value.max_call_duration_speech
    .map((item) => item.message.trim())
    .filter(isNotBlank)

  const replacements = conversationFlow.value.replacements
    .filter((item) => isNotBlank(item.key))
    .reduce<Record<string, string>>((acc, item) => {
      acc[item.key] = item.value || ''
      return acc
    }, {})

  return {
    general_information: {
      ...generalInformation.value,
      ...(experimental.value.tools.custom_skill && {
        agent_description: experimental.value.contextual_prompt_override ?? '',
      }),
    },
    conversational_flow: {
      has_welcome_message: conversationFlow.value.has_welcome_message,
      has_wrap_up_message: conversationFlow.value.has_wrap_up_message,
    },
    customer_intents: customerIntents.value,
    inactivity: inactivity.value,
    call_duration_limits: {
      max_call_duration_seconds: callDurationLimits.value.max_call_duration_seconds,
      max_call_duration_speech: maxCallDurationSpeech.length ? maxCallDurationSpeech : undefined,
      max_call_duration_output: callDurationLimits.value.max_call_duration_output,
      max_concurrent_calls: callDurationLimits.value.max_concurrent_calls,
    },
    knowledge: {
      ...(experimental.value.tools.answer_with_rag
        ? {
            document_ids: knowledge.value.document_ids?.length
              ? [...new Set(knowledge.value.document_ids)]
              : [],
          }
        : {}),
      custom_output: knowledge.value.custom_output ?? 'ANSWER_FROM_KB',
      custom_output_unsuccessful: knowledge.value.custom_output_unsuccessful ?? 'FAIL_FROM_KB',
    },
    experimental: {
      fsm: experimental.value.fsm,
      form_schema: (() => {
        const filled = experimental.value.form_schema.filter(
          (f) => isNotBlank(f.name) || isNotBlank(f.description),
        )
        return filled.length > 0 ? filled : undefined
      })(),
      sampling: {
        temperature: experimental.value.sampling.temperature,
        top_k: experimental.value.sampling.top_k,
        top_p: experimental.value.sampling.top_p,
        ...(experimental.value.sampling.generation_model === GENERATION_MODEL_V2 && {
          max_tokens: experimental.value.sampling.max_tokens,
        }),
      },
      flags: experimental.value.flags,
      tools: {
        common: Object.entries(experimental.value.tools)
          .filter(([key, enabled]) => enabled && key !== 'custom_skill')
          .map(([key]) => key),
      },
      features: {
        asr_model: experimental.value.transcription_model,
        generation_model: experimental.value.sampling.generation_model ?? DEFAULT_GENERATION_MODEL,
        voice_isolation_model:
          experimental.value.voice_isolation_model === VoiceIsolationModel.None
            ? null
            : experimental.value.voice_isolation_model,
        ...((experimental.value.voice_isolation_model === VoiceIsolationModel.Dtln ||
          experimental.value.voice_isolation_model === VoiceIsolationModel.DeepFilterNet3) && {
          noise_suppression_strength: experimental.value.noise_suppression_strength,
        }),
        km_embedded_rag: experimental.value.km_embedded_rag,
        km_enable_rerank: experimental.value.km_enable_rerank,
        km_expand_query: experimental.value.km_expand_query,
        km_similarity_threshold: experimental.value.km_similarity_threshold,
        km_number_of_doc_chunks: experimental.value.km_number_of_doc_chunks,
        vad_activation_threshold: experimental.value.vad.activation_threshold,
        vad_min_speech_duration: experimental.value.vad.min_speech_duration,
        ...Object.fromEntries(
          experimental.value.feature_toggles
            .filter(({ key }) => isNotBlank(key))
            .map(({ key, value }) => [key, value]),
        ),
      },
      additional_parameters: experimental.value.tools.custom_skill
        ? serializeAdditionalParams(experimental.value.additional_parameters)
        : undefined,
      persona_uuid_override:
        experimental.value.tools.custom_skill &&
        isNotBlank(experimental.value.persona_uuid_override) &&
        (isNotBlank(experimental.value.persona_role_override) ||
          isNotBlank(experimental.value.persona_run_tools_on_enter) ||
          isNotBlank(experimental.value.persona_voice_ids_override))
          ? experimental.value.persona_uuid_override
          : undefined,
      persona_role_override:
        experimental.value.tools.custom_skill &&
        isNotBlank(experimental.value.persona_role_override)
          ? experimental.value.persona_role_override
          : undefined,
      persona_run_tools_on_enter: experimental.value.tools.custom_skill
        ? parseRunToolsOnEnter(experimental.value.persona_run_tools_on_enter)
        : undefined,
      persona_voice_ids_override: experimental.value.tools.custom_skill
        ? parseRunToolsOnEnter(experimental.value.persona_voice_ids_override)
        : undefined,
    },
    external_tools:
      externalTools.value.servers.length > 0
        ? serializeExternalTools(externalTools.value)
        : undefined,
    enabled: isEnabled.value,
    speech: (() => {
      const si = experimental.value.speech_interruptions
      const hasReplacements = Object.keys(replacements).length > 0
      return {
        ...(hasReplacements ? { replacements } : {}),
        interruptible_welcome: si.welcome,
        interruptible_wrap_up: si.wrap_up,
        interruptible_re_engagement: si.re_engagement,
        interruptible_max_duration: si.max_duration,
      }
    })(),
  }
})

const setConfigurationId = (id: string | null) => {
  configurationId.value = id
}

const setGeneralInformation = (data: GeneralInformation) => {
  generalInformation.value = data
}

type ConversationFlowInput = ConversationalFlow &
  Partial<{
    opening_phrases: string[] | { opening_phrase: string }[]
    closing_phrases: string[] | { closing_phrase: string }[]
  }>

const setConversationFlow = (data: ConversationFlowInput | ConversationalFlowUI) => {
  conversationFlow.value = {
    has_welcome_message: data.has_welcome_message,
    has_wrap_up_message: data.has_wrap_up_message,
    opening_phrases: Array.isArray(data.opening_phrases)
      ? data.opening_phrases.map((phrase: string | { opening_phrase: string }) =>
          typeof phrase === 'string' ? { opening_phrase: phrase } : phrase,
        )
      : [],
    closing_phrases: Array.isArray(data.closing_phrases)
      ? data.closing_phrases.map((phrase: string | { closing_phrase: string }) =>
          typeof phrase === 'string' ? { closing_phrase: phrase } : phrase,
        )
      : [],
    replacements: data.replacements
      ? Array.isArray(data.replacements)
        ? data.replacements
        : Object.entries(data.replacements).map(([key, value]) => ({ key, value }))
      : [],
  }
}

const setCustomerIntents = (data: CustomerIntent[]) => {
  if (data.length === 0) {
    const { createDefaultIntent } = useDefaultIntent()
    customerIntents.value = [createDefaultIntent(), createNoMatchIntent()]
    return
  }
  const configuredIntents = data.filter((intent) => !isNoMatchIntent(intent))
  const existingFallback = data.find(isNoMatchIntent)
  customerIntents.value = [...configuredIntents, existingFallback ?? createNoMatchIntent()]
}

const setInactivity = (data: RawInactivity) => {
  inactivity.value = {
    user_away_timeout: data.user_away_timeout,
    max_retries_user_away: data.max_retries_user_away,
    // Handle legacy property name: reengagement_description -> re_engagement_description
    re_engagement_description:
      data.re_engagement_description ?? data.reengagement_description ?? '',
    inactivity_output: data.inactivity_output,
  }
}

const setCallDurationLimits = (data: CallDurationLimits) => {
  callDurationLimits.value = {
    max_call_duration_seconds: data.max_call_duration_seconds,
    max_call_duration_speech: Array.isArray(data.max_call_duration_speech)
      ? data.max_call_duration_speech.map((msg: string) => ({ message: msg }))
      : [],
    max_call_duration_output: data.max_call_duration_output,
    max_concurrent_calls:
      data.max_concurrent_calls ?? DEFAULT_CALL_DURATION_LIMITS.max_concurrent_calls,
  }
}

const setKnowledge = (data: Knowledge) => {
  knowledge.value = data
}

const setExternalTools = (data: ExternalToolsPayload | undefined) => {
  externalTools.value = deserializeExternalTools(data)
}

const setExperimental = (data: RawExperimental | null | undefined) => {
  if (!data) return
  const commonTools: string[] = data.tools?.common ?? []
  const hasAnyMainSkill =
    commonTools.includes('answer_with_rag') ||
    commonTools.includes('qualify_call') ||
    commonTools.includes('step_validation')
  const mainSkills = normalizeMainSkills({
    answer_with_rag: commonTools.includes('answer_with_rag'),
    qualify_call: commonTools.includes('qualify_call'),
    step_validation: commonTools.includes('step_validation'),
    custom_skill: !hasAnyMainSkill,
  })

  // Transform flags.enabled from array to object for the UI
  let flagsEnabled = DEFAULT_EXPERIMENTAL.flags.enabled
  if (data.flags?.enabled && Array.isArray(data.flags.enabled)) {
    // Backend format: enabled: ["ok", "confusion"]
    // Transform to UI format: enabled: { ok: true, confusion: true, ... }
    const enabledArray = data.flags.enabled
    flagsEnabled = {
      ok: enabledArray.includes('ok'),
      confusion: enabledArray.includes('confusion'),
      hostility: enabledArray.includes('hostility'),
      dissatisfaction: enabledArray.includes('dissatisfaction'),
      complaint: enabledArray.includes('complaint'),
      vulgarity: enabledArray.includes('vulgarity'),
      fraud: enabledArray.includes('fraud'),
    }
  } else if (data.flags?.enabled && typeof data.flags.enabled === 'object') {
    // Already in UI format (for backward compatibility)
    flagsEnabled = data.flags.enabled as FlagsEnabled
  }
  experimental.value = {
    form_schema:
      data.form_schema && data.form_schema.length > 0
        ? (data.form_schema as VariableUI[])
        : [{ ...DEFAULT_FORM_SCHEMA_FIELD }],
    fsm: data.fsm ?? DEFAULT_EXPERIMENTAL.fsm,
    sampling: {
      generation_model:
        (data.features?.generation_model as string | undefined) ?? DEFAULT_GENERATION_MODEL,
      temperature: data.sampling?.temperature ?? DEFAULT_EXPERIMENTAL.sampling.temperature,
      top_k: data.sampling?.top_k ?? DEFAULT_EXPERIMENTAL.sampling.top_k,
      top_p: data.sampling?.top_p ?? DEFAULT_EXPERIMENTAL.sampling.top_p,
      max_tokens:
        data.features?.generation_model === GENERATION_MODEL_V2
          ? data.sampling?.max_tokens
          : undefined,
    },
    flags: {
      detection_window: data.flags?.detection_window ?? DEFAULT_EXPERIMENTAL.flags.detection_window,
      enabled: flagsEnabled,
    },
    tools: {
      end_call: data.tools?.common.includes('end_call') ?? DEFAULT_EXPERIMENTAL.tools.end_call,
      append_to_session_memory:
        data.tools?.common.includes('append_to_session_memory') ??
        DEFAULT_EXPERIMENTAL.tools.append_to_session_memory,
      text_to_numbers:
        data.tools?.common.includes('text_to_numbers') ??
        DEFAULT_EXPERIMENTAL.tools.text_to_numbers,
      set_speech_language:
        data.tools?.common.includes('set_speech_language') ??
        DEFAULT_EXPERIMENTAL.tools.set_speech_language,
      ...mainSkills,
    },
    transcription_model: isTranscriptionModel(data.features?.asr_model)
      ? data.features.asr_model
      : DEFAULT_EXPERIMENTAL.transcription_model,
    voice_isolation_model: isVoiceIsolationModel(data.features?.voice_isolation_model)
      ? data.features.voice_isolation_model
      : data.features?.voice_isolation_model === null
        ? VoiceIsolationModel.None
        : DEFAULT_EXPERIMENTAL.voice_isolation_model,
    noise_suppression_strength: (data.features?.noise_suppression_strength ??
      DEFAULT_EXPERIMENTAL.noise_suppression_strength) as number,
    // Backward compat: km_embedded_rag was previously stored in tools.common
    km_embedded_rag: (data.features?.km_embedded_rag ??
      data.tools?.common.includes('km_embedded_rag') ??
      DEFAULT_EXPERIMENTAL.km_embedded_rag) as boolean,
    km_enable_rerank: (data.features?.km_enable_rerank ??
      DEFAULT_EXPERIMENTAL.km_enable_rerank) as boolean,
    km_expand_query: (data.features?.km_expand_query ??
      DEFAULT_EXPERIMENTAL.km_expand_query) as boolean,
    km_similarity_threshold: (data.features?.km_similarity_threshold ??
      DEFAULT_EXPERIMENTAL.km_similarity_threshold) as number,
    km_number_of_doc_chunks: (data.features?.km_number_of_doc_chunks ??
      DEFAULT_EXPERIMENTAL.km_number_of_doc_chunks) as number,
    // Backward compat: vad was previously stored in a separate vad object
    vad: {
      min_speech_duration: (data.features?.vad_min_speech_duration ??
        data.vad?.min_speech_duration ??
        DEFAULT_EXPERIMENTAL.vad.min_speech_duration) as number,
      activation_threshold: (data.features?.vad_activation_threshold ??
        data.vad?.activation_threshold ??
        DEFAULT_EXPERIMENTAL.vad.activation_threshold) as number,
    },
    feature_toggles: data.features
      ? Object.entries(data.features as Record<string, unknown>)
          .filter(([k]) => !KNOWN_FEATURE_KEYS.has(k))
          .map(([key, val]) => ({ key, value: String(val) }))
      : [],
    contextual_prompt_override: data.contextual_prompt_override ?? data._agentDescription ?? '',
    additional_parameters: data.additional_parameters
      ? Object.entries(data.additional_parameters).map(([key, value]) => ({
          key,
          value,
        }))
      : [],
    class_identifier_override:
      data.class_identifier &&
      data.class_identifier !== 'qualification_scenario' &&
      data.class_identifier !== 'validation_scenario'
        ? data.class_identifier
        : '',
    persona_uuid_override: data.persona_uuid_override ?? '',
    persona_role_override: data.persona_role_override ?? '',
    persona_run_tools_on_enter: data.persona_run_tools_on_enter
      ? JSON.stringify(data.persona_run_tools_on_enter, null, 2)
      : '',
    persona_voice_ids_override: data.persona_voice_ids_override
      ? JSON.stringify(data.persona_voice_ids_override, null, 2)
      : '',
    speech_interruptions: {
      welcome: data._speech?.interruptible_welcome ?? true,
      wrap_up: data._speech?.interruptible_wrap_up ?? true,
      re_engagement: data._speech?.interruptible_re_engagement ?? true,
      max_duration: data._speech?.interruptible_max_duration ?? true,
    },
  }
}

const isNewConfiguration = computed(() => {
  return !configurationId.value || configurationId.value.trim() === '' || isDuplicated.value
})

const saveInitialConfiguration = (currentConfig: VirtualAgentConfiguration) => {
  initialPayload.value = JSON.parse(JSON.stringify(currentConfig)) as VirtualAgentConfiguration
}

const hasUnsavedChanges = computed(() => {
  return JSON.stringify(payload.value) !== JSON.stringify(initialPayload.value)
})
const hasPendingChanges = computed(() => {
  return initialPayload.value !== null && hasUnsavedChanges.value
})

const resetConfiguration = () => {
  setDuplicated(false)
  configurationId.value = ''
  generalInformation.value = { ...DEFAULT_GENERAL_INFORMATION }
  conversationFlow.value = { ...DEFAULT_CONVERSATION_FLOW }
  customerIntents.value = []
  inactivity.value = { ...DEFAULT_INACTIVITY }
  callDurationLimits.value = { ...DEFAULT_CALL_DURATION_LIMITS }
  knowledge.value = {}
  experimental.value = structuredClone(DEFAULT_EXPERIMENTAL)
  externalTools.value = structuredClone(DEFAULT_EXTERNAL_TOOLS)

  // Clean up draft from sessionStorage when leaving the configurator
  sessionStorage.removeItem(STORAGE_KEYS.CONFIGURATOR_DRAFT)
}

watch(
  [
    payload,
    () => conversationFlow.value.opening_phrases,
    () => conversationFlow.value.closing_phrases,
  ],
  ([newPayload]) => {
    // Only sync config to sessionStorage when on the configurator page
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    if (!window.location?.pathname?.includes('/configurator')) return

    const draft = {
      configId: configurationId.value ?? null,
      payload: {
        ...newPayload,
        conversational_flow: {
          ...newPayload.conversational_flow,
          opening_phrases: conversationFlow.value.opening_phrases,
          closing_phrases: conversationFlow.value.closing_phrases,
        },
      },
    }
    sessionStorage.setItem(STORAGE_KEYS.CONFIGURATOR_DRAFT, JSON.stringify(draft))
  },
  { deep: true },
)

const loadConfigFromStorage = (expectedId?: string | null): boolean => {
  const stored = sessionStorage.getItem(STORAGE_KEYS.CONFIGURATOR_DRAFT)
  if (!stored) return false

  try {
    const draft = JSON.parse(stored) as ConfiguratorDraft
    // Verify the draft belongs to the same config (avoid cross-tab conflicts)
    const draftId = draft.configId ?? null
    const currentId = expectedId ?? null
    if (draftId !== currentId) return false

    const parsed: VirtualAgentConfiguration = draft.payload
    const gi = parsed.general_information
    setGeneralInformation(
      isNotBlank(parsed.experimental?.contextual_prompt_override)
        ? { ...gi, agent_description: '' }
        : gi,
    )
    setConversationFlow(parsed.conversational_flow)
    setCustomerIntents(parsed.customer_intents)
    setInactivity(parsed.inactivity)
    setCallDurationLimits(parsed.call_duration_limits)
    if (parsed.knowledge) setKnowledge(parsed.knowledge)
    if (parsed.experimental)
      setExperimental({
        ...parsed.experimental,
        _agentDescription: parsed.general_information.agent_description,
        _speech: parsed.speech,
      })
    setExternalTools(parsed.external_tools)
    return true
  } catch (e) {
    console.warn('Failed to restore config from storage:', e)
    return false
  }
}
const setEnabled = (value: boolean) => {
  isEnabled.value = value
}

const setDuplicated = (value: boolean) => {
  isDuplicated.value = value
}

const requestCancelNavigation = (to: string) => {
  pendingNavigationTo.value = to
  isCancelModalOpen.value = true
}

const closeCancelModal = () => {
  isCancelModalOpen.value = false
  pendingNavigationTo.value = null
}

export const useConfigurator = () => {
  return {
    generalInformation,
    conversationFlow,
    customerIntents,
    inactivity,
    callDurationLimits,
    knowledge,
    experimental,
    externalTools,
    payload,
    isNewConfiguration,
    configurationId,
    resetConfiguration,
    setConfigurationId,
    setGeneralInformation,
    setConversationFlow,
    setCustomerIntents,
    setInactivity,
    setCallDurationLimits,
    setKnowledge,
    setExperimental,
    setExternalTools,
    hasUnsavedChanges,
    hasPendingChanges,
    saveInitialConfiguration,
    loadConfigFromStorage,
    setEnabled,
    isDuplicated,
    setDuplicated,
    isCancelModalOpen,
    pendingNavigationTo,
    requestCancelNavigation,
    closeCancelModal,
  }
}
